import React, { useState, useCallback } from 'react';
import { X, Link, Globe, ExternalLink, FolderOpen } from 'lucide-react';
import PopupTransition from '@/components/ui/PopupTransition';
import { useAppDispatch } from '@/data/store/utils/redux';
import { createFileAsync } from '@/data/store/actions';
import { TFilePayload } from '@/data/types';

interface ExternalUrlModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPath: string;
  onUploadSuccess?: () => void;
}

const ExternalUrlModal: React.FC<ExternalUrlModalProps> = ({ isOpen, onClose, currentPath, onUploadSuccess }) => {
  const dispatch = useAppDispatch();
  const [url, setUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleClose = useCallback(() => {
    setUrl('');
    setFileName('');
    setDescription('');
    setError('');
    onClose();
  }, [onClose]);

  const isValidUrl = useCallback((urlString: string): boolean => {
    try {
      new URL(urlString);
      return true;
    } catch {
      return false;
    }
  }, []);

  const extractFileNameFromUrl = useCallback((urlString: string): string => {
    try {
      const url = new URL(urlString);
      const pathname = url.pathname;
      const filename = pathname.split('/').pop() || '';
      return filename || 'External Link';
    } catch {
      return 'External Link';
    }
  }, []);

  const handleUrlChange = useCallback(
    (value: string) => {
      setUrl(value);
      setError('');

      // Auto-generate filename if not manually set
      if (value && isValidUrl(value) && !fileName) {
        const suggestedName = extractFileNameFromUrl(value);
        setFileName(suggestedName);
      }
    },
    [fileName, isValidUrl, extractFileNameFromUrl],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!url.trim()) {
        setError('Το URL είναι υποχρεωτικό');
        return;
      }

      if (!isValidUrl(url.trim())) {
        setError('Παρακαλώ εισάγετε έγκυρο URL');
        return;
      }

      if (!fileName.trim()) {
        setError('Το όνομα αρχείου είναι υποχρεωτικό');
        return;
      }

      setIsCreating(true);
      setError('');

      try {
        const payload: TFilePayload = {
          name: fileName.trim(),
          path: currentPath,
          import_url: url.trim(),
          description: description.trim(),
          content_type: 'external/link',
        };

        await dispatch(createFileAsync({ payload }));

        if (onUploadSuccess) {
          onUploadSuccess();
        }

        handleClose();
      } catch (error) {
        console.error('External URL creation failed:', error);
        setError('Αποτυχία δημιουργίας εξωτερικού συνδέσμου');
      } finally {
        setIsCreating(false);
      }
    },
    [url, fileName, description, currentPath, dispatch, onUploadSuccess, handleClose, isValidUrl],
  );

  return (
    <PopupTransition open={isOpen} onClose={handleClose}>
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-8 py-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="rounded-2xl bg-white/20 p-3 backdrop-blur-sm">
                <ExternalLink className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Προσθήκη εξωτερικού συνδέσμου</h3>
                <p className="mt-1 text-sm text-blue-100">Συνδέστε εξωτερικά αρχεία και ιστοσελίδες στη συλλογή σας</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isCreating}
              className="rounded-xl p-2 text-white/80 transition-all duration-200 hover:bg-white/20 hover:text-white disabled:opacity-50"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Subtitle */}
        <div className="border-b border-gray-100 bg-blue-50/50 px-8 py-4">
          <div className="flex items-center gap-3">
            <FolderOpen className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-800">
              Τοποθεσία: {currentPath === '/' ? 'Root Directory' : currentPath}
            </span>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-8">
          <div className="space-y-6">
            {/* URL Input */}
            <div>
              <label htmlFor="externalUrl" className="mb-3 block text-sm font-semibold text-gray-800">
                URL αρχείου ή ιστοσελίδας
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-4">
                  <Link className="h-5 w-5 text-blue-500" />
                </div>
                <input
                  id="externalUrl"
                  type="url"
                  value={url}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  placeholder="https://example.com/document.pdf"
                  className="w-full rounded-xl border-2 border-gray-200 py-3 pr-4 pl-12 text-gray-900 placeholder-gray-400 transition-all duration-200 hover:border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none"
                  autoFocus
                  disabled={isCreating}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Εισάγετε το πλήρες URL του αρχείου ή της ιστοσελίδας που θέλετε να συνδέσετε
              </p>
            </div>

            {/* File Name Input */}
            <div>
              <label htmlFor="fileName" className="mb-3 block text-sm font-semibold text-gray-800">
                Όνομα εμφάνισης
              </label>
              <input
                id="fileName"
                type="text"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                placeholder="π.χ. Οδηγίες χρήσης, Αναφορά, Παρουσίαση..."
                className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 transition-all duration-200 hover:border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none"
                disabled={isCreating}
              />
              <p className="mt-2 text-xs text-gray-500">Επιλέξτε ένα περιγραφικό όνομα για τον σύνδεσμο</p>
            </div>

            {/* Description Input */}
            <div>
              <label htmlFor="description" className="mb-3 block text-sm font-semibold text-gray-800">
                Περιγραφή <span className="font-normal text-gray-500">(προαιρετικά)</span>
              </label>
              <textarea
                id="description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Σύντομη περιγραφή του περιεχομένου του συνδέσμου..."
                className="w-full resize-none rounded-xl border-2 border-gray-200 px-4 py-3 text-gray-900 placeholder-gray-400 transition-all duration-200 hover:border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 focus:outline-none"
                disabled={isCreating}
              />
            </div>

            {/* Preview */}
            {url && isValidUrl(url) && (
              <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-blue-100 p-2">
                    <Globe className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="mb-2 text-sm font-semibold text-blue-900">Προεπισκόπηση συνδέσμου</p>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs font-medium text-blue-700">URL:</p>
                        <p className="rounded bg-white/50 px-2 py-1 font-mono text-sm break-all text-blue-800">{url}</p>
                      </div>
                      {fileName && (
                        <div>
                          <p className="text-xs font-medium text-blue-700">Όνομα εμφάνισης:</p>
                          <p className="text-sm font-semibold text-blue-800">{fileName}</p>
                        </div>
                      )}
                      {description && (
                        <div>
                          <p className="text-xs font-medium text-blue-700">Περιγραφή:</p>
                          <p className="text-sm text-blue-800">{description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="rounded-xl border-2 border-red-200 bg-red-50 p-4">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-red-100 p-2">
                    <X className="h-4 w-4 text-red-600" />
                  </div>
                  <p className="text-sm font-medium text-red-700">{error}</p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 flex justify-end gap-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={isCreating}
              className="rounded-xl border-2 border-gray-300 px-6 py-3 text-sm font-semibold text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 focus:outline-none disabled:opacity-50"
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              disabled={!url.trim() || !fileName.trim() || isCreating}
              className="rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg focus:ring-4 focus:ring-blue-200 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-none"
            >
              {isCreating ? (
                <span className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Δημιουργία...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Προσθήκη συνδέσμου
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </PopupTransition>
  );
};

export default ExternalUrlModal;
